import { RolePermission } from '../types/interface.d';

export interface ProjectModeAccess {
  canViewMeasurements: boolean;
  canManageWorkflow: boolean;
  canToggleModes: boolean;
  defaultMode: 'view' | 'operation';
}

/**
 * Utility function to check project mode access based on user permissions
 * @param permissions - Array of user's role permissions
 * @returns Object containing access flags and default mode
 */
export const getProjectModeAccess = (permissions: RolePermission[]): ProjectModeAccess => {
  // Check for manage workflow permission (for manage mode)
  const canViewMeasurements = permissions.some(
    (permission) =>
      permission.name.toUpperCase() === 'MANAGE_WORKFLOW' &&
      (permission.permission.includes('READ') || permission.permission.includes('WRITE'))
  );

  // Check for projects permission (for operation mode)
  const canManageWorkflow = permissions.some(
    (permission) =>
      permission.name.toUpperCase() === 'PROJECTS' &&
      permission.permission.includes('READ')
  );

  // User can toggle if they have access to both sections
  const canToggleModes = canViewMeasurements && canManageWorkflow;

  // Determine default mode based on permissions
  let defaultMode: 'view' | 'operation' = 'view';

  if (canViewMeasurements && !canManageWorkflow) {
    defaultMode = 'view';
  } else if (!canViewMeasurements && canManageWorkflow) {
    defaultMode = 'operation';
  } else if (canViewMeasurements && canManageWorkflow) {
    // If user has both, default to view mode
    defaultMode = 'view';
  }

  return {
    canViewMeasurements,
    canManageWorkflow,
    canToggleModes,
    defaultMode,
  };
};

/**
 * Check if user has the required permission for a specific project mode
 * @param permissions - Array of user's role permissions
 * @param mode - The project mode to check ('view' | 'operation')
 * @returns Boolean indicating if user has access to the specified mode
 */
export const hasProjectModeAccess = (
  permissions: RolePermission[],
  mode: 'view' | 'operation'
): boolean => {
  const access = getProjectModeAccess(permissions);

  if (mode === 'view') {
    return access.canViewMeasurements;
  } else if (mode === 'operation') {
    return access.canManageWorkflow;
  }

  return false;
};